import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import styles from './Dashboard.module.css';

const OUTPUT_LABELS = {
  sbar:              'SBAR',
  artsconsult:       'Artsconsult',
  rapportage:        'Verpleegkundige rapportage',
  dienst_overdracht: 'Dienst-overdracht',
  familie:           'Familiecommunicatie',
  overplaatsing:     'Overplaatsingsoverdracht',
};

const URGENTIE_CONFIG = {
  acuut:            { label: 'Acuut',           kleur: '#CC0000', bg: '#FFF0F0' },
  spoed:            { label: 'Spoed',            kleur: '#C65A00', bg: '#FFF8E6' },
  niet_spoedeisend: { label: 'Niet spoedeisend', kleur: '#1A6B3C', bg: '#F0FBF4' },
};

export default function Dashboard() {
  const { gebruiker, heeftPermissie } = useAuth();
  const navigate = useNavigate();
  const [recenteOutputs, setRecenteOutputs] = useState([]);
  const [laden, setLaden] = useState(true);
  const [platformUpdates, setPlatformUpdates] = useState([]);

  const isSuperadmin = heeftPermissie('superadmin.alles');
  const isBeheerder  = heeftPermissie('beheer.gebruikers');
  const kanSBAR      = heeftPermissie('outputs.sbar');
  const kanInzien    = heeftPermissie('outputs.inzien');

  useEffect(() => {
    axios.get('/api/platform-updates/recent')
      .then(res => setPlatformUpdates(res.data.updates || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (kanSBAR || kanInzien) {
      axios.get('/api/outputs/mijn')
        .then(res => {
          // Flatten sessies naar outputs voor dashboard weergave
          const sessies = res.data.sessies || [];
          const outputs = sessies.flatMap(s =>
            (s.outputs || []).map(o => ({
              ...o,
              urgentie: s.urgentie,
              omschrijving: s.omschrijving,
              is_oefenmodus: s.is_oefenmodus,
            }))
          );
          // Sorteer op datum, neem de 5 meest recente
          outputs.sort((a, b) => new Date(b.aangemaakt_op) - new Date(a.aangemaakt_op));
          setRecenteOutputs(outputs.slice(0, 5));
        })
        .catch(() => {})
        .finally(() => setLaden(false));
    } else {
      setLaden(false);
    }
  }, [kanSBAR, kanInzien]);

  const dagdeel = () => {
    const uur = new Date().getHours();
    if (uur < 6)  return 'Goedenacht';
    if (uur < 12) return 'Goedemorgen';
    if (uur < 18) return 'Goedemiddag';
    return 'Goedenavond';
  };

  return (
    <div className={styles.pagina}>

      {/* Welkom header */}
      <div className={styles.welkom}>
        <div className={styles.welkomTekst}>
          <h1 className={styles.welkomTitel}>
            {dagdeel()}, {gebruiker?.naam?.split(' ')[0] || gebruiker?.email}
          </h1>
          <p className={styles.welkomSub}>
            {new Date().toLocaleDateString('nl-NL', { weekday:'long', day:'numeric', month:'long', year:'numeric' })}
          </p>
        </div>
      </div>

      <div className={styles.layout}>
        <div className={styles.hoofdKolom}>

          {/* Snelkoppelingen */}
          <div className={styles.sectie}>
            <h2 className={styles.sectieTitel}>Snelkoppelingen</h2>
            <div className={styles.snelKoppelingenGrid}>
              {kanSBAR && (
                <button className={`${styles.snelKnop} ${styles.primair}`}
                  onClick={() => navigate('/beoordeling')}>
                  <span className={styles.snelIcoon}>🩺</span>
                  <span className={styles.snelLabel}>Nieuwe beoordeling</span>
                  <span className={styles.snelSub}>ABCDE invoer starten</span>
                </button>
              )}
              <button className={styles.snelKnop} onClick={() => navigate('/outputs')}>
                <span className={styles.snelIcoon}>📋</span>
                <span className={styles.snelLabel}>Mijn outputs</span>
                <span className={styles.snelSub}>Eerdere rapportages bekijken</span>
              </button>
              {isBeheerder && (
                <button className={styles.snelKnop} onClick={() => navigate('/admin/gebruikers')}>
                  <span className={styles.snelIcoon}>👥</span>
                  <span className={styles.snelLabel}>Gebruikersbeheer</span>
                  <span className={styles.snelSub}>Gebruikers beheren</span>
                </button>
              )}
              {isBeheerder && (
                <button className={styles.snelKnop} onClick={() => navigate('/admin/rollen')}>
                  <span className={styles.snelIcoon}>🔑</span>
                  <span className={styles.snelLabel}>Rollenbeheer</span>
                  <span className={styles.snelSub}>Rollen en permissies</span>
                </button>
              )}
              {isSuperadmin && (
                <button className={`${styles.snelKnop} ${styles.superadmin}`}
                  onClick={() => navigate('/superadmin')}>
                  <span className={styles.snelIcoon}>⚙️</span>
                  <span className={styles.snelLabel}>Superadmin</span>
                  <span className={styles.snelSub}>Platformbeheer</span>
                </button>
              )}
            </div>
          </div>

          {/* Recente outputs */}
          {(kanSBAR || kanInzien) && (
            <div className={styles.sectie}>
              <div className={styles.sectieHeader}>
                <h2 className={styles.sectieTitel}>Recente outputs</h2>
                <button className={styles.allesKnop} onClick={() => navigate('/outputs')}>
                  Alle outputs →
                </button>
              </div>
              {laden ? (
                <div className={styles.laadTekst}>Laden...</div>
              ) : recenteOutputs.length === 0 ? (
                <div className={styles.leeg}>
                  <p>Nog geen outputs gegenereerd.</p>
                  <button className={styles.knopPrimair} onClick={() => navigate('/beoordeling')}>
                    Start eerste beoordeling
                  </button>
                </div>
              ) : (
                <div className={styles.outputLijst}>
                  {recenteOutputs.map(output => {
                    const urg = URGENTIE_CONFIG[output.urgentie];
                    return (
                      <div key={output.id} className={styles.outputRij}
                        onClick={() => navigate('/outputs')}>
                        <div className={styles.outputInfo}>
                          <span className={styles.outputType}>
                            {OUTPUT_LABELS[output.output_type]}
                          </span>
                          {urg && (
                            <span className={styles.urgentieBadge}
                              style={{ color: urg.kleur, background: urg.bg }}>
                              {urg.label}
                            </span>
                          )}
                          {output.is_oefenmodus === 1 && (
                            <span className={styles.oefenBadge}>Oefening</span>
                          )}
                        </div>
                        <span className={styles.outputDatum}>
                          {new Date(output.aangemaakt_op).toLocaleDateString('nl-NL')}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Zijkolom: platform updates */}
        <div className={styles.zijKolom}>
          <div className={styles.sectie}>
            <h2 className={styles.sectieTitel}>Platform updates</h2>
            <div className={styles.updateLijst}>
              {platformUpdates.length === 0 ? (
                <div className={styles.updateLeeg}>Geen updates beschikbaar.</div>
              ) : (
                platformUpdates.map(update => (
                  <div key={update.id} className={styles.updateKaart}>
                    <div className={styles.updateHeader}>
                      <span className={styles.updateVersie}>{update.versie}</span>
                      <span className={styles.updateDatum}>
                        {new Date(update.datum).toLocaleDateString('nl-NL', { month:'long', year:'numeric' })}
                      </span>
                    </div>
                    <div className={styles.updateTitel}>{update.titel}</div>
                    <div className={styles.updateInhoud}>{update.omschrijving}</div>
                  </div>
                ))
              )}
              <button className={styles.alleUpdatesKnop}
                onClick={() => navigate('/updates')}>
                Alle updates →
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
